import { Modal, Alert } from 'antd';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useState, useEffect, useRef } from 'react';
import { logout, logoutUser, hideSessionTimeout, setRedirectAfterLogin } from '../../slices/authSlice';
import websocketService from '../../services/websocketService';

const AUTO_LOGOUT_SECONDS = 60;

const SessionTimeoutModal = ({ variant = 'timeout' }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { showSessionTimeoutModal } = useSelector((state) => state.auth);
  const [countdown, setCountdown] = useState(AUTO_LOGOUT_SECONDS);
  const logoutFiredRef = useRef(false);

  useEffect(() => {
    if (!showSessionTimeoutModal) {
      logoutFiredRef.current = false;
      return;
    }
    setCountdown(AUTO_LOGOUT_SECONDS);
    const timer = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) { clearInterval(timer); handleOk(); return 0; }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [showSessionTimeoutModal]);

  const handleOk = async () => {
    if (logoutFiredRef.current) return;
    logoutFiredRef.current = true;
    // Fire synchronous event so components can save state before logout
    window.dispatchEvent(new CustomEvent('auth:beforeLogout'));

    // Store current URL before redirecting (for redirect after re-login)
    const currentPath = window.location.pathname + window.location.search;
    // Per-tab copy (sessionStorage is unique per tab) so each tab restores to ITS OWN page after a
    // cross-tab re-login - the shared localStorage copy gets overwritten when several tabs time out.
    try { sessionStorage.setItem('restorePathAfterRelogin', currentPath); } catch { /* ignore */ }
    // Call helper function directly - NOT a Redux action
    setRedirectAfterLogin(currentPath);
    
    // Hide the modal and clear localStorage flag
    dispatch(hideSessionTimeout());
    // Stop late socket auth errors and clear Redux auth state before the async server logout.
    websocketService.disconnect();
    dispatch(logout());
    // Fully clear local auth state and server httpOnly cookies before showing login.
    await dispatch(logoutUser());
    // Navigate to login using React Router (no full page reload)
    navigate('/login', { replace: true });
  };

  const handleCancel = () => {
    // Same as OK - user must login again
    handleOk();
  };

  return (
    <Modal
      title={
        <span style={{ color: '#262626', fontWeight: 600 }}>
          <ExclamationCircleOutlined style={{ marginRight: 8, color: '#faad14' }} />
          {variant === 'auth-error' ? 'Session Invalid' : 'Session Expired'}
        </span>
      }
      open={showSessionTimeoutModal}
      onOk={handleOk}
      onCancel={handleCancel}
      closable={false}
      maskClosable={false}
      centered={true}
      okText="Log In Again"
      okButtonProps={{ 
        type: 'primary',
        style: { backgroundColor: '#1890ff', borderColor: '#1890ff' }
      }}
      cancelButtonProps={{ style: { display: 'none' } }}
      // Modal will persist until user explicitly clicks the button
      // No auto-logout timeout - user must acknowledge the timeout
    >
      <div style={{ padding: '8px 0' }}>
        <p style={{ fontSize: '14px', marginBottom: '16px' }}>
          {variant === 'auth-error'
            ? 'Your session is no longer valid. Please log in again to continue.'
            : <span>You will be logged out in <strong>{countdown}</strong> seconds.</span>
          }
        </p>
        
        {/* Warning about unsaved data */}
        <Alert
          message="Warning: All Unsaved Data Will Be Lost"
          description="Any unsaved changes in forms or documents will be permanently lost. Please log in again to continue working."
          type="warning"
          showIcon
          style={{ marginBottom: '16px' }}
        />
        
        <p style={{ marginTop: '12px', fontSize: '12px', color: '#666', fontStyle: 'italic' }}>
          Note: You must click "Log In Again" to continue. Your previous page will be saved for redirect after login.
        </p>
      </div>
    </Modal>
  );
};

export default SessionTimeoutModal;
